import { eventTracker } from 'app/modules/Segment';
import {
  buildJusfyEventsContext,
  buildJusfyEventsProperties,
} from 'app/modules/Segment/buildJusfyEvent';
import {
  FeatureEnum,
  TContext,
  TProperties,
} from 'app/modules/Segment/types';
import {
  DEFAULT_HELP_URL,
  getYouTubeVideoId,
  normalizePathname,
  normalizeUrl,
} from './helpers';

export const EVENT_TYPES = {
  BACK_CLICK: 'back_click',
  TUTORIAL_CLICK: 'tutorial_click',
  HELP_CLICK: 'help_click',
} as const;

export type HeaderNavigationTrackingEvent =
  typeof EVENT_TYPES[keyof typeof EVENT_TYPES];

type TrackEventCallback = (event: HeaderNavigationTrackingEvent) => void;

interface BaseTrackEventArgs {
  onTrackEvent?: TrackEventCallback;
}

interface HelpClickArgs {
  helpUrl?: string;
}

interface TutorialClickArgs {
  tutorialVideoUrl?: string;
  tutorialEmbedUrl?: string | null;
  setIsTutorialLoading: (value: boolean) => void;
  setIsTutorialOpen: (value: boolean) => void;
}

const EVENTS = {
  HEADER_BACK_CLICKED: 'header_back_clicked',
  HEADER_HELP_CLICKED: 'header_help_clicked',
  HEADER_TUTORIAL_CLICKED: 'header_tutorial_clicked',
} as const;

const HEADER_NAVIGATION_LIBRARY_NAME = 'header-navigation-library';

function buildContext(component: string): TContext {
  return buildJusfyEventsContext({
    libraryName: HEADER_NAVIGATION_LIBRARY_NAME,
    component,
  });
}

interface SharedTrackingProperties {
  feature?: FeatureEnum;
  featureContext?: string;
  pageTitle?: string;
  isSecondaryScreen?: boolean;
}

function buildProperties(
  event: string,
  feature: FeatureEnum,
  eventMeaning: string,
  shared: SharedTrackingProperties = {},
  extraProperties: Partial<TProperties> = {},
): TProperties {
  return buildJusfyEventsProperties({
    event,
    feature,
    eventMeaning,
    relevantUserActivity: true,
    extraProperties: {
      feature_context: shared.featureContext,
      page_title: shared.pageTitle,
      is_secondary_screen: shared.isSecondaryScreen ?? false,
      ...extraProperties,
    },
  });
}

function trackHeaderBackClicked(
  shared: SharedTrackingProperties,
  pathname?: string,
): void {
  const event = EVENTS.HEADER_BACK_CLICKED;

  eventTracker.track({
    event,
    properties: buildProperties(
      event,
      shared.feature ?? FeatureEnum.DASHBOARD,
      'Usuário clicou no botão de voltar da navegação de cabeçalho.',
      shared,
      {
        pathname: normalizePathname(pathname),
      },
    ),
    context: buildContext('back-button-component'),
  });
}

function trackHeaderHelpClicked(
  shared: SharedTrackingProperties,
  helpUrl?: string,
): void {
  const event = EVENTS.HEADER_HELP_CLICKED;
  const normalizedHelpUrl = normalizeUrl(helpUrl || DEFAULT_HELP_URL);

  eventTracker.track({
    event,
    properties: buildProperties(
      event,
      FeatureEnum.HELP_CENTER,
      'Usuário clicou no botão de ajuda da navegação de cabeçalho.',
      shared,
      normalizedHelpUrl ? { help_url: normalizedHelpUrl } : {},
    ),
    context: buildContext('help-button-component'),
  });
}

function trackHeaderTutorialClicked(
  shared: SharedTrackingProperties,
  tutorialVideoUrl?: string,
): void {
  const event = EVENTS.HEADER_TUTORIAL_CLICKED;
  const normalizedTutorialUrl = normalizeUrl(tutorialVideoUrl);
  const videoId = getYouTubeVideoId(tutorialVideoUrl);

  eventTracker.track({
    event,
    properties: buildProperties(
      event,
      shared.feature ?? FeatureEnum.NOT_FEATURE,
      'Usuário clicou no botão de tutorial da navegação de cabeçalho.',
      shared,
      {
        ...(normalizedTutorialUrl
          ? { tutorial_url: normalizedTutorialUrl }
          : {}),
        ...(videoId ? { tutorial_video_id: videoId } : {}),
      },
    ),
    context: buildContext('tutorial-button-component'),
  });
}

function notifyTrackEvent(
  event: HeaderNavigationTrackingEvent,
  onTrackEvent?: TrackEventCallback,
): void {
  onTrackEvent?.(event);
}

export function emitBackNavigationClickEvent({
  onTrackEvent,
  feature,
  featureContext,
  pageTitle,
  isSecondaryScreen,
}: BaseTrackEventArgs & SharedTrackingProperties): void {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : undefined;

  trackHeaderBackClicked(
    { feature, featureContext, pageTitle, isSecondaryScreen },
    pathname,
  );
  notifyTrackEvent(EVENT_TYPES.BACK_CLICK, onTrackEvent);
}

export function emitTutorialNavigationClickEvent({
  tutorialVideoUrl,
  onTrackEvent,
  feature,
  featureContext,
  pageTitle,
  isSecondaryScreen,
}: {
  tutorialVideoUrl?: string;
  onTrackEvent?: TrackEventCallback;
} & SharedTrackingProperties): void {
  trackHeaderTutorialClicked(
    { feature, featureContext, pageTitle, isSecondaryScreen },
    tutorialVideoUrl,
  );
  notifyTrackEvent(EVENT_TYPES.TUTORIAL_CLICK, onTrackEvent);
}

export function emitHelpNavigationClickEvent({
  helpUrl,
  onTrackEvent,
  featureContext,
  pageTitle,
  isSecondaryScreen,
}: HelpClickArgs & BaseTrackEventArgs & SharedTrackingProperties): void {
  const safeTargetUrl = resolveSafeHelpUrl(helpUrl);

  trackHeaderHelpClicked(
    { featureContext, pageTitle, isSecondaryScreen },
    safeTargetUrl,
  );
  notifyTrackEvent(EVENT_TYPES.HELP_CLICK, onTrackEvent);
}

function resolveSafeHelpUrl(helpUrl?: string): string {
  let safeTargetUrl = DEFAULT_HELP_URL;

  if (typeof window !== 'undefined') {
    try {
      const parsed = new URL(
        helpUrl || DEFAULT_HELP_URL,
        window.location.origin,
      );
      if (['http:', 'https:'].includes(parsed.protocol)) {
        safeTargetUrl = parsed.toString();
      }
    } catch {
      safeTargetUrl = DEFAULT_HELP_URL;
    }
  }

  return safeTargetUrl;
}

export function handleBackNavigationClick(): void {
  if (typeof window !== 'undefined') {
    window.history.back();
  }
}

export function handleTutorialNavigationClick({
  tutorialEmbedUrl,
  setIsTutorialLoading,
  setIsTutorialOpen,
}: TutorialClickArgs): void {
  if (tutorialEmbedUrl) {
    setIsTutorialLoading(true);
    setIsTutorialOpen(true);
  }
}

export function handleHelpNavigationClick({ helpUrl }: HelpClickArgs): void {
  const safeTargetUrl = resolveSafeHelpUrl(helpUrl);

  if (typeof window !== 'undefined') {
    window.open(safeTargetUrl, '_blank', 'noopener,noreferrer');
  }
}
